"use client";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Image,
  Button,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  useColorModeValue,
  Skeleton,
  SkeletonCircle,
  Badge,
  Icon,
  useBreakpointValue,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Select,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import {
  FaBriefcase,
  FaMapMarkerAlt,
  FaUsers,
  FaGlobe,
  FaIndustry,
  FaFilter,
} from "react-icons/fa";
import {
  fetchCompanyListAsync,
  selectCompanies,
  selectTotalCompanies,
} from "./CompanySlice/CompanyListSlice";
import { Link } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  UsersIcon,
  GlobeAltIcon,
  BriefcaseIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline"
import companylogo from "./logo1png.png"
const CompanyCard = ({ company, base_url, onViewJobs }) => {
  const cardBg = useColorModeValue("white", "gray.800")
  const textColor = useColorModeValue("gray.600", "gray.300")
  const headingColor = useColorModeValue("gray.800", "white")
  const badgeBg = useColorModeValue("blue.50", "blue.900")
  const badgeColor = useColorModeValue("blue.600", "blue.200")

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      bg={cardBg}

    >
      <Box px={"6"}>
        <Flex alignItems="center" mb={4} py={"2"}>
          <Image
            borderRadius="full"
            boxSize="80px"
            // src={`${base_url}${company.company_logo}`}
            src={company.company_logo ? `${base_url}${company.company_logo}` : companylogo}
            onError={(e) => (e.target.src = companylogo)}

            // alt={company.company_name}
            mr={4}
            objectFit="contain"
            // fallbackSrc="https://via.placeholder.com/80"
            border={"1"}
          />
          <VStack align="start" spacing={0}>
            <Link to={`/company-list/${company.uuid}`}>

              <Heading as="h3" size="md" color={headingColor}>
                {company?.company_name || "Company Name Not Mentioned"}
              </Heading>
            </Link>
            <Badge bg={badgeBg} color={badgeColor} fontSize="xs" px={2} py={1}>
              {company?.industry_type || "Industry Not Mentioned"}
            </Badge>
          </VStack>
        </Flex>
        <VStack align="start" spacing={0} >
          <Tooltip label="Company Type" placement="top">
            <Flex align="start">
              <Box as={BriefcaseIcon} w={5} h={5} color={textColor} mr={2} />
              <Text fontSize="sm" color={textColor}>
                {company.company_type || "Company Type"}
              </Text>
            </Flex>
          </Tooltip>
          <Tooltip label="Location" placement="top">
            <Flex align="start">
              <Box as={MapPinIcon} w={5} h={5} color={textColor} mr={2} />
              <Text fontSize="sm" color={textColor}>
                {company.city?.name || "City"}, {company.state?.name || "State"}, {company.country?.name || "Country"}
              </Text>
            </Flex>
          </Tooltip>
          <Tooltip label="Employee Count" placement="top">
            <Flex align="start">
              <Box as={UsersIcon} w={5} h={5} color={textColor} mr={2} />
              <Text fontSize="sm" color={textColor}>
                {company.number_of_employee || "N/A"} employees
              </Text>
            </Flex>
          </Tooltip>
          <Tooltip label="Website" placement="top">
            <Flex align="start" w="100%">
              <Box as={GlobeAltIcon} w={5} h={5} color={textColor} mr={2} />
              <Text fontSize="sm" color={textColor} isTruncated maxW="100%">
                {company.website || "Website not available"}
              </Text>
            </Flex>
          </Tooltip>
        </VStack>
      </Box>
      <Box borderTopWidth="1px" p={4}>
        <Link to={`/company-list/${company.uuid}`}>

          <Button
            width="full"
            colorScheme="blue"
            leftIcon={<Box as={BriefcaseIcon} w={5} h={5} />}
          // onClick={() => onViewJobs(company)}
          >
            View {company.jobs_count} Open Jobs
          </Button>
        </Link>
      </Box>
    </Box>
  )
}

const CompanySkeleton = () => (
  <Box borderWidth="1px" borderRadius="lg" overflow="hidden" boxShadow="lg">
    <Box p={6}>
      <Flex mb={4}>
        <SkeletonCircle size="80px" mr={4} />
        <VStack align="start" flex={1} spacing={2}>
          <Skeleton height="24px" width="80%" />
          <Skeleton height="16px" width="60%" />
        </VStack>
      </Flex>
      <VStack align="start" spacing={3}>
        <HStack spacing={4} width="100%">
          <Skeleton height="16px" width="40%" />
          <Skeleton height="16px" width="40%" />
        </HStack>
        <Skeleton height="16px" width="70%" />
      </VStack>
    </Box>
    <Box borderTopWidth="1px" p={4}>
      <Skeleton height="40px" width="100%" />
    </Box>
  </Box>
);

export function CompanyList() {
  const dispatch = useDispatch();
  const companies = useSelector(selectCompanies);
  const totalCompanies = useSelector(selectTotalCompanies);

  const base_url = "https://api.indiahirings.com/uploads/company/";
  const [searchTerm, setSearchTerm] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedCompany, setSelectedCompany] = useState(null);

  const jobsPerPage = 15; // Number of jobs per page
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(totalCompanies / jobsPerPage);

  // Fetch jobs whenever the current page changes
  useEffect(() => {
    dispatch(fetchCompanyListAsync(currentPage));
    window.scrollTo(0, 0)
  }, [dispatch, currentPage]);
  // console.log("companies", companies)

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const headerBg = useColorModeValue("blue.500", "blue.900");
  const headerTextColor = useColorModeValue("white", "gray.100");
  const subTextColor = useColorModeValue("blue.100", "blue.200");

  const columns = useBreakpointValue({ base: 1, md: 2, lg: 3 });

  const handleViewJobs = (company) => {
    setSelectedCompany(company);
    onOpen();
  };



  return (
    <Box bg={bgColor} minH="100vh">
      <Box bg={headerBg} py={10} mb={4}>
        <Container maxW="container.xl">
          <VStack spacing={6} align="start">
            <Heading as="h1" size="2xl" color={headerTextColor}>
              Explore Top Companies
            </Heading>
            <Text fontSize="xl" color={subTextColor}>
              Discover opportunities at leading organizations
            </Text>

          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={8}>
        {companies ? (
          <>
            <Flex justify="space-between" align="center" mb={6}>
              <Heading as="h2" size="lg">
                {companies.length} Companies Found
              </Heading>
            </Flex>
            <SimpleGrid columns={columns} spacing={3}>
              {companies.map((company) => (
                <CompanyCard
                  key={company.uuid}
                  company={company}
                  base_url={base_url}
                  onViewJobs={handleViewJobs}
                />
              ))}
            </SimpleGrid>
            <div className="flex justify-evenly items-center mt-4">
              <button
                className={`bg-gray-300 text-black px-4 py-2 rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className={`bg-blue-500 text-white px-4 py-2 rounded ${currentPage === totalPages
                  ? "opacity-50 cursor-not-allowed"
                  : ""
                  }`}
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </>
        ) : companies ? (
          <Box textAlign="center" py={10}>
            <Heading as="h2" size="xl" mb={4}>
              No companies found
            </Heading>
            <Text fontSize="lg" color="gray.500">
              We couldn't find any companies matching your criteria. Please try
              adjusting your search or filters.
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={columns} spacing={8}>
            {[...Array(6)].map((_, index) => (
              <CompanySkeleton key={index} />
            ))}
          </SimpleGrid>
        )}
      </Container>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedCompany?.company_name} - Open Positions
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedCompany?.jobs_count > 0 ? (
              <VStack align="start" spacing={4}>
                <Text>
                  Here are the open positions at {selectedCompany.company_name}:
                </Text>
                {/* You would typically fetch and display actual job listings here */}
                <Text>• Software Engineer</Text>
                <Text>• Product Manager</Text>
                <Text>• Data Analyst</Text>
                <Button colorScheme="blue" leftIcon={<FaGlobe />}>
                  View All Jobs on Company Website
                </Button>
              </VStack>
            ) : (
              <Text>
                No open positions found for this company at the moment.
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}